import log from "../../../utils/logger";

/**
 * IndexedDBStorage provides a simple key-value storage interface using IndexedDB.
 * It supports quota management, key size reporting, and basic CRUD operations.
 */
class IndexedDBStorage {
  /** Name of the IndexedDB database */
  private dbName: string;
  /** Name of the object store within the database */
  private storeName: string;
  /** Promise resolving to the opened IndexedDB database */
  private dbPromise: Promise<IDBDatabase>;

  /**
   * Constructs a new IndexedDBStorage instance.
   * @param dbName - Name of the database (default: "IndexedDBStorage")
   * @param storeName - Name of the object store (default: "keyvalue")
   */
  constructor(dbName = "IndexedDBStorage", storeName = "keyvalue") {
    this.dbName = dbName;
    this.storeName = storeName;
    // Start opening the database immediately
    this.dbPromise = this.openDB();
  }

  /**
   * Opens the IndexedDB database and creates the object store if needed.
   * @returns Promise resolving to the opened IDBDatabase
   */
  private openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);
      request.onupgradeneeded = () => {
        const db = request.result;
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Helper to run a callback with the object store in a transaction.
   * @param mode - Transaction mode ("readonly", "readwrite", or "versionchange")
   * @param callback - Function receiving the object store and returning an IDBRequest
   * @returns Promise resolving to the result of the request
   */
  private async withStore<T>(
    mode: "readonly" | "readwrite" | "versionchange",
    callback: (store: IDBObjectStore) => IDBRequest<T>
  ): Promise<T> {
    const db = await this.dbPromise;
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(this.storeName, mode);
      const store = tx.objectStore(this.storeName);
      const request = callback(store);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Print the size (in megabytes) of data stored for each key.
   * Useful for debugging and monitoring storage usage.
   */
  async printKeySizes(): Promise<void> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, "readonly");
      const store = tx.objectStore(this.storeName);
      const request = store.openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const key = cursor.key;
          const value = cursor.value;
          // Estimate size using Blob for both strings and objects
          let size = 0;
          if (typeof value === "string") {
            size = new Blob([value]).size;
          } else {
            size = new Blob([JSON.stringify(value)]).size;
          }
          const sizeMB = (size / (1024 * 1024)).toFixed(4);
          log.debug(`Key: ${key}, Size: ${sizeMB} MB`);
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get a value by key.
   * @param key - The key to retrieve
   * @returns Promise resolving to the value (string) or null if not found
   */
  async getItem(key: string): Promise<string | null> {
    return this.withStore("readonly", (store) => store.get(key));
  }

  /**
   * Set a value by key, ensuring quota is not exceeded.
   * @param key - The key to set
   * @param value - The value to store
   * @returns Promise resolving to true if successful, false otherwise
   */
  async setItem(key: string, value: string): Promise<boolean> {
    try {
      // Check if there is enough quota before storing
      const byteSize = new Blob([value]).size;
      await this.ensureQuota(byteSize);
      await this.withStore("readwrite", (store) => store.put(value, key));
      return true;
    } catch (error) {
      log.error(`Error setting item in IndexedDB: ${error}`);
      return false;
    }
  }

  /**
   * Remove a value by key.
   * @param key - The key to remove
   */
  async removeItem(key: string): Promise<void> {
    await this.withStore("readwrite", (store) => store.delete(key));
  }

  /**
   * Clear all values in the store.
   */
  async clear(): Promise<void> {
    await this.withStore("readwrite", (store) => store.clear());
  }

  /**
   * Get the key at a specific index.
   * @param index - The index of the key
   * @returns Promise resolving to the key (string) or null if not found
   */
  async key(index: number): Promise<string | null> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, "readonly");
      const store = tx.objectStore(this.storeName);
      let i = 0;
      const request = store.openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) return resolve(null);
        if (i === index) return resolve(cursor.key as string);
        i++;
        cursor.continue();
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get the number of items in the store.
   * @returns Promise resolving to the number of items
   */
  async length(): Promise<number> {
    return this.withStore("readonly", (store) => store.count());
  }

  /**
   * Calculate the total bytes used by all values in the store.
   * @returns Promise resolving to the total bytes used
   */
  private async getUsedBytes(): Promise<number> {
    const db = await this.dbPromise;
    return new Promise((resolve, reject) => {
      const tx = db.transaction(this.storeName, "readonly");
      const store = tx.objectStore(this.storeName);
      let total = 0;
      const request = store.openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          const value = cursor.value;
          // Estimate size using Blob for both strings and objects
          if (typeof value === "string") {
            total += new Blob([value]).size;
          } else {
            total += new Blob([JSON.stringify(value)]).size;
          }
          cursor.continue();
        } else {
          resolve(total);
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get storage usage and quota (uses StorageManager if available).
   * For safety, 5% is subtracted from the quota to avoid hitting the hard limit.
   * @returns Promise resolving to an object with usage and quota in bytes
   */
  private async getQuota(): Promise<{ usage: number; quota: number }> {
    if (navigator.storage && navigator.storage.estimate) {
      const { usage = 0, quota = 0 } = await navigator.storage.estimate();
      // Apply 5% slack for safety
      return { usage, quota: quota * 0.95 };
    }
    // Fallback: estimate based on used bytes and a default quota
    const usage = await this.getUsedBytes();
    const quota = 50 * 1024 * 1024 * 0.95; // 50MB default, minus 5% slack
    return { usage, quota };
  }

  /**
   * Ensure there is enough quota for a new value.
   * Throws an error if quota would be exceeded.
   * @param newValueSize - Size in bytes of the new value to be stored
   */
  private async ensureQuota(newValueSize: number): Promise<void> {
    const { usage, quota } = await this.getQuota();
    if (usage + newValueSize > quota) {
      throw new Error("Quota exceeded");
    }
  }
}

// Export a singleton instance of IndexedDBStorage for use in the app
export default new IndexedDBStorage("ms-word-addin", "storage");
